import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { PageHeader, Card, Button } from './ui';
import { getRedressStats, RedressStats } from '../api/redressStats';
import './AdminRedressStats.css';

const AdminRedressStats: React.FC = () => {
  const { isAdmin } = useAuth();
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState<RedressStats | null>(null);
  const [error, setError] = useState('');

  const handleQuery = async () => {
    setLoading(true);
    setError('');
    setStats(null);
    try {
      const data = await getRedressStats();
      setStats(data);
    } catch (err: any) {
      console.error('Failed to load redress stats:', err);
      setError(err.response?.data?.error || 'Failed to load stats');
    } finally {
      setLoading(false);
    }
  };

  if (!isAdmin) {
    return <div className="redress-admin-required">Admin access required.</div>;
  }

  const pct = (n: number, total: number) => total > 0 ? ((n / total) * 100).toFixed(1) : '0.0';

  return (
    <div>
      <style>{`
        .redress-card { background: #fff; border: 1px solid #ddd; border-radius: 5px; padding: 20px; margin-bottom: 20px; }
        .redress-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; margin-top: 16px; }
        .redress-stat { background: #f8f9fa; border-radius: 6px; padding: 16px; text-align: center; }
        .redress-stat-value { font-size: 28px; font-weight: 700; color: #2c3e50; }
        .redress-stat-label { font-size: 13px; color: #6c757d; margin-top: 4px; }
        .redress-eligible { border-left: 4px solid #28a745; }
        .redress-outside { border-left: 4px solid #dc3545; }
        .redress-nodate { border-left: 4px solid #ffc107; }
        .redress-cancelled { border-left: 4px solid #6c757d; }
        .redress-btn { background: #007bff; color: #fff; border: none; padding: 10px 24px; border-radius: 4px; cursor: pointer; font-size: 14px; }
        .redress-btn:hover { background: #0056b3; }
        .redress-btn:disabled { background: #ccc; cursor: not-allowed; }
        .redress-alert-error { background: #f8d7da; color: #721c24; border: 1px solid #f5c6cb; padding: 12px; border-radius: 4px; margin-bottom: 16px; }
        .redress-section-title { font-size: 16px; font-weight: 600; color: #2c3e50; margin-bottom: 12px; }
      `}</style>

      <PageHeader
        title="Redress Scheme Stats"
        description="Query claims against the FCA motor finance redress scheme date ranges."
      />

      <Card className="redress-section">
        <Button variant="primary" onClick={handleQuery} disabled={loading}>
          {loading ? 'Querying claims...' : 'Run Query'}
        </Button>

        {loading && (
          <p className="redress-loading-note">
            Querying all claims from Cosmos DB, this may take a moment...
          </p>
        )}
      </Card>

      {error && <div className="redress-alert-error">{error}</div>}

      {stats && (
        <>
          <Card className="redress-section">
            <div className="redress-section-title">Active Claims (non-cancelled)</div>
            <div className="redress-grid">
              <div className="redress-stat redress-eligible">
                <div className="redress-stat-value">{stats.active_claims.eligible.toLocaleString()}</div>
                <div className="redress-stat-label">Eligible (inside scheme)</div>
                <div className="redress-stat-label">{pct(stats.active_claims.eligible, stats.active_claims.total)}% of active</div>
              </div>
              <div className="redress-stat">
                <div className="redress-stat-value">{stats.active_claims.total.toLocaleString()}</div>
                <div className="redress-stat-label">Total Active Claims</div>
              </div>
            </div>

            <div className="redress-grid redress-grid--5 redress-grid--mt">
              <div className="redress-stat redress-outside">
                <div className="redress-stat-value">{stats.active_claims.before_scheme.toLocaleString()}</div>
                <div className="redress-stat-label">Before Scheme</div>
                <div className="redress-stat-label">Pre 6 Apr 2007</div>
              </div>
              <div className="redress-stat redress-eligible">
                <div className="redress-stat-value">{stats.active_claims.scheme_1.toLocaleString()}</div>
                <div className="redress-stat-label">Scheme 1</div>
                <div className="redress-stat-label">{stats.scheme_dates.scheme_1}</div>
              </div>
              <div className="redress-stat redress-eligible">
                <div className="redress-stat-value">{stats.active_claims.scheme_2.toLocaleString()}</div>
                <div className="redress-stat-label">Scheme 2</div>
                <div className="redress-stat-label">{stats.scheme_dates.scheme_2}</div>
              </div>
              <div className="redress-stat redress-outside">
                <div className="redress-stat-value">{stats.active_claims.after_scheme.toLocaleString()}</div>
                <div className="redress-stat-label">After Scheme</div>
                <div className="redress-stat-label">Post 1 Nov 2024</div>
              </div>
              <div className="redress-stat redress-nodate">
                <div className="redress-stat-value">{stats.active_claims.no_date.toLocaleString()}</div>
                <div className="redress-stat-label">No Date</div>
                <div className="redress-stat-label">Missing credit date</div>
              </div>
            </div>
          </Card>

          <Card className="redress-section">
            <div className="redress-section-title">Cancelled Claims</div>
            <div className="redress-grid redress-grid--5">
              <div className="redress-stat redress-cancelled">
                <div className="redress-stat-value">{stats.cancelled_claims.before_scheme.toLocaleString()}</div>
                <div className="redress-stat-label">Before Scheme</div>
              </div>
              <div className="redress-stat redress-cancelled">
                <div className="redress-stat-value">{stats.cancelled_claims.scheme_1.toLocaleString()}</div>
                <div className="redress-stat-label">Scheme 1</div>
              </div>
              <div className="redress-stat redress-cancelled">
                <div className="redress-stat-value">{stats.cancelled_claims.scheme_2.toLocaleString()}</div>
                <div className="redress-stat-label">Scheme 2</div>
              </div>
              <div className="redress-stat redress-cancelled">
                <div className="redress-stat-value">{stats.cancelled_claims.after_scheme.toLocaleString()}</div>
                <div className="redress-stat-label">After Scheme</div>
              </div>
              <div className="redress-stat redress-cancelled">
                <div className="redress-stat-value">{stats.cancelled_claims.no_date.toLocaleString()}</div>
                <div className="redress-stat-label">No Date</div>
              </div>
            </div>
            <p className="redress-cancelled-total">
              Total cancelled: {stats.cancelled_claims.total.toLocaleString()}
            </p>
          </Card>

          <Card className="redress-section redress-card--muted">
            <p className="redress-grand-total">
              Grand total across all claims: <strong className="redress-grand-total-value">{stats.grand_total.toLocaleString()}</strong>
            </p>
          </Card>
        </>
      )}
    </div>
  );
};

export default AdminRedressStats;
